import {
  Agreement,
  AgreementSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  partyRoleSchema,
  targetSchema,
} from '@hero/schema';
import { API } from '..';
import { z } from 'zod';
import { agreementDatesSchema } from '@hero/schema';

//* get one
export const agreementGetOneAPISchema = AgreementSchema.model
  .pick({
    _id: true,
    status: true,
    type: true,
  })
  .extend({
    /** the organization id for the agreement */
    orgId: objectIdSchema,
  })
  .partial()
  .required({ _id: true });
export type AgreementGetOneAPI = API<z.infer<typeof agreementGetOneAPISchema>, Agreement>;
//* get many
export const agreementGetManyAPISchema = agreementGetOneAPISchema
  .omit({ _id: true })
  .extend(paginationWithSearchSchema.shape)
  .extend({
    /**
     * Filter by any party id present on the agreement (matches against
     * the denormalized `tenants[]` array — see
     * `AgreementSchema.shape.tenants`). Use to drive "agreements
     * involving this Org / Client / Profile" in workspace + drawer
     * views without forcing the caller to know which side of `parties[]`
     * the id sits on.
     */
    partyId: objectIdSchema.optional(),
  });
export type AgreementGetManyAPI = API<z.infer<typeof agreementGetManyAPISchema>, Agreement[]>;

//* create
/**
 * Counterparty input for {@link agreementCreateAPISchema}. The caller
 * provides the source-doc id + canonical {@link targetSchema} value +
 * roles[]; the service materializes a {@link Party} via
 * `PartyResolverService.makeParty` so the snapshot invariants are
 * enforced server-side (primary id duplicated into `ids[]`, name
 * copied from the resolved source doc, etc.).
 *
 * Kept narrow on purpose — callers should NOT hand-roll a full
 * `Party` payload. The org-side party is built from the `orgId`
 * field.
 */
export const agreementCreateCounterpartySchema = z.object({
  _id: objectIdSchema,
  target: targetSchema,
  /** primary role first; ≥ 1 enforced. */
  roles: z.array(partyRoleSchema).min(1),
  /** display name override; otherwise the resolver uses the source doc's name. */
  name: z.string().optional(),
});
export type AgreementCreateCounterparty = z.infer<typeof agreementCreateCounterpartySchema>;

/**
 * `routes` is no longer embedded in `Agreement` — it lives on the `Route`
 * collection keyed by `agreementId`. The `routes` field is therefore
 * removed from this schema; callers create the agreement first, then push
 * Routes through `RouteCreateAPI` with the resulting `agreementId`.
 *
 * `counterparty` is REQUIRED. The Agreement model is bilateral
 * (`parties` is a 2-tuple); the org-side party is materialized from
 * `orgId` and the counterparty from this field.
 */
export const agreementCreateAPISchema = AgreementSchema.data
  .omit({ currency: true, org: true, dates: true, parties: true, tenants: true, connectionId: true })
  .partial()
  .extend({
    /**
     * REQUIRED — `Agreement.dates` is now non-optional on the model
     * (see `AgreementSchema.shape.dates`). Callers pass `start` /
     * `end` / `autoRenew`; the service computes `durationInDays` from
     * the pair so it stays in sync with the window.
     */
    dates: agreementDatesSchema.omit({ durationInDays: true }),
    /** the organization id for the agreement (org-side party) */
    orgId: objectIdSchema,
    /** the OTHER party in the bilateral agreement; service materializes the Party snapshot. */
    counterparty: agreementCreateCounterpartySchema,
  });
export type AgreementCreateAPI = API<z.infer<typeof agreementCreateAPISchema>, Agreement>;

//* update
export const agreementUpdateAPISchema = agreementCreateAPISchema
  .omit({ orgId: true, counterparty: true })
  .partial()
  .extend({
    /** the agreement id to update */
    _id: objectIdSchema,
  });
export type AgreementUpdateAPI = API<z.infer<typeof agreementUpdateAPISchema>, Agreement>;
